import Message from 'src/app/models/Message'
import { UnifiedMessageRequest } from '../messageApiClient'

// Lazada-specific supported message types
export const LAZADA_SUPPORTED_TYPES = ['text', 'image'] as const
export type LazadaMessageType = typeof LAZADA_SUPPORTED_TYPES[number]

/**
 * LazadaMessageHelper
 *
 * Platform-specific helper for Lazada messages.
 * Handles validation and request formatting specific to Lazada.
 */
export class LazadaMessageHelper {
  /**
   * Validate message type for Lazada platform
   * Lazada only supports: text, image
   */
  static validateMessageType(messageType: string): boolean {
    return LAZADA_SUPPORTED_TYPES.includes(messageType as any)
  }

  /**
   * Get human-readable error for unsupported types
   */
  static getUnsupportedTypeError(messageType: string): string {
    return (
      `Message type '${messageType}' is not supported for Lazada. ` +
      `Supported types: ${LAZADA_SUPPORTED_TYPES.join(', ')}`
    )
  }

  /**
   * Build Lazada-specific request body
   * Formats message data according to Lazada requirements
   */
  static buildRequestBody(
    sessionUserId: string,
    message: Message
  ): UnifiedMessageRequest {
    const requestBody: UnifiedMessageRequest = {
      messaging_product: 'lazada',
      to: sessionUserId,
      type: message.messageType!,
    }

    // Add content based on message type
    switch (message.messageType) {
      case 'text':
        requestBody.text = {
          body: message.textContent,  // Changed from "text" to "body" to match API spec
          preview_url: false,
        }
        break

      case 'image':
        if (!message.mediaUrl) {
          throw new Error('Image URL is required for image messages')
        }
        requestBody.image = {
          link: message.mediaUrl,
        }
        break

      default:
        throw new Error(this.getUnsupportedTypeError(message.messageType!))
    }

    return requestBody
  }

  /**
   * Validate all Lazada message requirements
   * Throws error if validation fails
   */
  static validate(message: Message, sessionUserId: string): void {
    // Validate session user ID (Lazada buyer ID)
    if (!sessionUserId || sessionUserId.trim() === '') {
      throw new Error('Session user ID is required for Lazada messages')
    }

    // Validate message type
    if (!message.messageType) {
      throw new Error('Message type is required')
    }

    if (!this.validateMessageType(message.messageType)) {
      throw new Error(this.getUnsupportedTypeError(message.messageType))
    }

    // Validate content based on type
    if (message.messageType === 'text' && !message.textContent) {
      throw new Error('Text content is required for text messages')
    }

    if (message.messageType === 'image' && !message.mediaUrl) {
      throw new Error('Media URL is required for image messages')
    }
  }
}
